/**
 * POSTLAUNCH-V3 · M5 (Fr 04.09.2026, 15:00) — Glossar-Modal.
 *
 * Reduziert kognitive Last: die App verwendet drei Kernbegriffe
 * (Anliegen · Gegenüber · Türöffner) plus einige Nebenbegriffe.
 * Wer den Sprech noch nicht kennt: ein Klick auf das "?" in der TopBar
 * öffnet dieses Modal.
 *
 * POSTLAUNCH-V4 (Do 03.09.2026, 22:35) — Inline-Verlinkung via Custom-Event:
 * jede Stelle im Code kann `window.dispatchEvent(new CustomEvent("f4s:glossar-open", { detail: { term } }))`
 * feuern, um das Modal an einem konkreten Begriff zu öffnen. `<GlossarLink term="..." />`
 * kapselt das für Fließtext.
 */
import React, { useEffect, useState, useRef } from "react";
import { BookOpen, X } from "lucide-react";

// Alle Begriffe zentral, damit GlossarLink Highlight + Modal-Fokus rendern kann.
const KERN = [
  {
    term: "Anliegen",
    hint: "Kernbegriff",
    body:
      "Deine konkrete Situation, in der Kommunikation ankommen soll — ein Gespräch, eine E-Mail, ein schwieriger Moment. Ein Anliegen wird im Anliegen-Tool zu einem Flexpfad.",
  },
  {
    term: "Gegenüber",
    hint: "Kernbegriff",
    body:
      "Die Person, mit der du gerade sprichst oder sprechen willst. Ihr Fokustyp (Hund · Eule · Katze · Biene) bestimmt, wie sie tickt und was sie öffnet oder schließt.",
  },
  {
    term: "Türöffner",
    hint: "Kernbegriff",
    body:
      "Der erste Satz, mit dem du bei deinem Gegenüber wirklich ankommst. Ein passender Türöffner öffnet die Tür — ein unpassender schließt sie.",
  },
];

const NEBEN = [
  {
    term: "Fokustyp",
    body:
      "Vier Fokustypen (Hund · Eule · Katze · Biene) beschreiben, worauf jemand achtet und wie er reagiert. Dein Fokustyp = wie du tickst. Der Fokustyp deines Gegenübers = wie er tickt.",
  },
  {
    term: "Flexpfad",
    body:
      "Der Weg von deinem Anliegen zum ersten Aufschlag: kurz beschreiben, was schiefläuft — Flex4Success zeigt dir passenden Ton, passende Frage, passenden Einstieg.",
  },
  {
    term: "Türsteher",
    body:
      "Was dein Gegenüber automatisch abwehrt: bei einer Katze z.B. Anweisungen von oben, bei einer Eule Emotion vor Fakten. Der Türsteher steht vor der Tür — der Türöffner umgeht ihn.",
  },
  {
    term: "Zugangscode",
    body:
      "Die passende Ansprache-Form für den Fokustyp deines Gegenübers: bei einem Hund Beziehung zuerst, bei einer Biene der klare nächste Schritt. Passt der Code, öffnet sich die Tür.",
  },
  {
    term: "Kommunikations-Framework",
    body:
      "Drei Ebenen plus Kontext, die jede Botschaft prägen: WAS · WIE · KÖRPERSPRACHE — ausgerichtet auf den Fokustyp-Kontext deines Gegenübers.",
  },
  {
    term: "Erster Aufschlag",
    body:
      "Dein konkreter erster Satz im Gespräch. Baut auf Türöffner + Kommunikations-Framework auf und ist das, womit du in die Situation gehst.",
  },
];

const ALL_TERMS = [...KERN.map((k) => k.term), ...NEBEN.map((n) => n.term)];

function slugifyTerm(term) {
  return term.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

export function openGlossar(term = null) {
  window.dispatchEvent(new CustomEvent("f4s:glossar-open", { detail: { term } }));
}

// Inline-Link: rendert Text als klickbaren Trigger mit dezenter Unterstreichung.
export function GlossarLink({ term, children, className = "" }) {
  const known = ALL_TERMS.includes(term);
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); openGlossar(term); }}
      data-testid={`glossar-inline-${slugifyTerm(term)}`}
      data-term={term}
      className={[
        "inline underline decoration-dotted decoration-[var(--fts-primary-bright)]/60 underline-offset-2",
        "hover:text-[var(--fts-primary-bright)] hover:decoration-solid transition-colors",
        known ? "cursor-help" : "cursor-not-allowed opacity-60",
        className,
      ].join(" ")}
      aria-label={`Glossar zu ${term} öffnen`}
      disabled={!known}
    >
      {children || term}
    </button>
  );
}

export default function Glossar({ open: controlledOpen, onClose }) {
  // Standalone-Kontrolle via Custom-Event UND kontrollierte Prop-Nutzung (TopBar).
  const [selfOpen, setSelfOpen] = useState(false);
  const [focusedTerm, setFocusedTerm] = useState(null);
  const termRefs = useRef({});

  // Prop überschreibt eigenen State, wenn gesetzt
  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : selfOpen;

  const close = () => {
    if (isControlled) onClose && onClose();
    else setSelfOpen(false);
    setFocusedTerm(null);
  };

  // Custom-Event-Listener für Inline-Links
  useEffect(() => {
    const handler = (e) => {
      const term = e.detail?.term || null;
      setFocusedTerm(term);
      if (!isControlled) setSelfOpen(true);
      else onClose && open ? null : null; // no-op, TopBar öffnet über Prop
    };
    window.addEventListener("f4s:glossar-open", handler);
    return () => window.removeEventListener("f4s:glossar-open", handler);
  }, [isControlled, onClose, open]);

  // Escape + Body-Scroll-Lock + Auto-Scroll zum fokussierten Begriff
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus-Term ins Sichtfeld scrollen
    if (focusedTerm) {
      requestAnimationFrame(() => {
        const el = termRefs.current[focusedTerm];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusedTerm]);

  if (!open) return null;

  const renderTerm = (t, isKern) => {
    const highlight = focusedTerm && focusedTerm.toLowerCase() === t.term.toLowerCase();
    return (
      <div
        key={t.term}
        ref={(el) => { if (el) termRefs.current[t.term] = el; }}
        data-testid={`glossar-term-${slugifyTerm(t.term)}`}
        data-focused={highlight ? "true" : "false"}
        className={[
          "rounded-xl p-4 transition-all",
          isKern
            ? "bg-[var(--fts-primary-bright)]/8 border border-[var(--fts-primary-bright)]/20"
            : "border border-transparent",
          highlight ? "ring-2 ring-[var(--fts-primary-bright)] shadow-md" : "",
        ].join(" ")}
      >
        <p className={isKern ? "text-sm font-extrabold text-slate-900 dark:text-white" : "text-sm font-bold text-slate-900 dark:text-white"}>
          {t.term}
          {isKern && (
            <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
              {t.hint}
            </span>
          )}
        </p>
        <p className={["mt-1 text-sm leading-snug", isKern ? "text-slate-700 dark:text-slate-200" : "text-slate-600 dark:text-slate-300"].join(" ")}>
          {t.body}
        </p>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto"
      data-testid="glossar-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="glossar-title"
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl my-8"
        data-testid="glossar-modal"
        data-focused-term={focusedTerm || ""}
      >
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[var(--fts-primary-bright)]" aria-hidden="true" />
            <h2 id="glossar-title" className="text-lg font-extrabold text-slate-900 dark:text-white">
              Glossar
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="glossar-close"
            aria-label="Schliessen"
            className="rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 p-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <section data-testid="glossar-kern">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)] mb-3">
              Drei Kernbegriffe
            </p>
            <div className="space-y-3">
              {KERN.map((k) => renderTerm(k, true))}
            </div>
          </section>

          <section data-testid="glossar-neben">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">
              Weitere Begriffe
            </p>
            <div className="space-y-3">
              {NEBEN.map((n) => renderTerm(n, false))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

